"use client"

import { useCallback, useRef } from "react"

/**
 * Publish one element's height as a CSS custom property on another.
 *
 * The demo stacks sticky layers: a bar at `top-0`, and beneath it a lever rail,
 * an impact panel and (now) a section rail that must all start below it. Those
 * offsets were three copies of a hardcoded `top-[72px]` — already approximate,
 * and guaranteed to drift the moment the bar wraps on a narrow viewport or
 * grows a row.
 *
 * This is the mechanism `packages/common/components/banner.tsx` already uses for
 * `--banner-height`, applied locally: a `ResizeObserver` writes the measured
 * height onto the scroll container, and everything below reads
 * `top-[var(--lp-bar-height)]`.
 *
 * Callback refs rather than `useRef` + `useEffect` on purpose — the observer has
 * to attach the moment both nodes exist, and a callback ref is the only hook
 * that fires exactly then, including when either node is swapped out.
 */
export function useMeasuredHeight(
    property: string,
): [(node: HTMLElement | null) => void, (node: HTMLElement | null) => void] {
    const sourceRef = useRef<HTMLElement | null>(null)
    const targetRef = useRef<HTMLElement | null>(null)
    const observerRef = useRef<ResizeObserver | null>(null)

    const sync = useCallback(() => {
        observerRef.current?.disconnect()
        observerRef.current = null

        const source = sourceRef.current
        const target = targetRef.current
        if (!source || !target) return

        const write = () => {
            target.style.setProperty(property, `${Math.round(source.getBoundingClientRect().height)}px`)
        }
        write()

        // `ResizeObserver` is not in every test environment (jsdom gained it
        // late), and a missing measurement is survivable — the CSS fallback in
        // `top-[var(--lp-bar-height,72px)]` covers it.
        if (typeof ResizeObserver === "undefined") return
        const observer = new ResizeObserver(write)
        observer.observe(source)
        observerRef.current = observer
    }, [property])

    const setSource = useCallback(
        (node: HTMLElement | null) => {
            sourceRef.current = node
            sync()
        },
        [sync],
    )

    const setTarget = useCallback(
        (node: HTMLElement | null) => {
            targetRef.current = node
            sync()
        },
        [sync],
    )

    return [setSource, setTarget]
}
